/**
 * career-assets — the Kompass inventory (route `/compass/assets`), the typed
 * Nachweise/Anlagen (certificates, references, portraits, portfolios — §3.5).
 *
 * Typed list (kind, title, confirmed state); add via upload (existing object-store
 * path); edit metadata; remove (tombstone — destructive, confirm affordance);
 * confirm AI-proposed classifications; promote an uploaded ingest document to a typed
 * asset. Confirm/remove are OPTIMISTIC store commands carrying the ABSOLUTE end-state
 * (status confirmed / removed); the store hydrates from `assets.list` (each asset
 * applied at its own monotonic `version`), so mutations reconcile from server truth.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { AssetStoreToken, I18nToken, SessionToken, apiGet, apiPost, apiUpload } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import { careerStream, type AssetKind, type AssetStore, type AssetView, type ShellSession } from "../domain"
import { degradedNote } from "../lib/degraded"

const ASSET_KINDS: AssetKind[] = ["certificate", "work_certificate", "reference", "diploma", "portfolio", "portrait", "other"]
const KIND_KEY: Record<AssetKind, CareerKey> = {
  certificate: "asset.kind.certificate",
  work_certificate: "asset.kind.workCertificate",
  reference: "asset.kind.reference",
  diploma: "asset.kind.diploma",
  portfolio: "asset.kind.portfolio",
  portrait: "asset.kind.portrait",
  other: "asset.kind.other",
}

interface PromotableDoc {
  ingestId: string
  documentId: string
  kind: string
}

export class AssetsVm implements ViewModel {
  readonly assets: ReadonlySignal<readonly AssetView[]>
  readonly promotable: Signal<readonly PromotableDoc[]> = signal<readonly PromotableDoc[]>([])
  /** Why the promotable-documents fan-out failed, if it did — the seam that separates
   *  "loaded, genuinely nothing to promote" (null + empty) from "we could not load this". */
  readonly promotableError: Signal<string | null> = signal<string | null>(null)
  readonly editingId: Signal<string | null> = signal<string | null>(null)
  /** The asset id awaiting a two-step remove confirmation (destructive — confirm affordance). */
  readonly removingId: Signal<string | null> = signal<string | null>(null)
  readonly addKind: Signal<AssetKind> = signal<AssetKind>("certificate")
  readonly busy: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  constructor(private readonly store: AssetStore, private readonly session: ShellSession, i18n: CareerI18n) {
    this.t = i18n.t
    this.assets = store.list("alle")
    this.renderDeps = [this.assets, this.promotable, this.promotableError, this.editingId, this.removingId, this.addKind, this.busy, this.error, this.loaded, store.pending]
  }

  private get userId(): string | null {
    return this.session.user?.sub ?? null
  }

  attach(): () => void {
    void this.refresh()
    return () => {}
  }

  /** Hydrate the store from the inventory list (each asset applied at its own version). */
  private async refresh(): Promise<void> {
    const userId = this.userId
    try {
      const assets = await apiGet<any[]>("/api/assets")
      if (userId) {
        for (const a of assets) {
          this.store.apply({
            streamId: careerStream(userId, "Asset", a.id),
            version: typeof a.version === "number" ? a.version : 1,
            type: "asset.Added",
            payload: { owner: userId, asset: a },
          })
        }
      }
      await this.loadPromotable()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  /** Uploaded ingest documents that can be promoted to typed assets — EXCLUDING any
   *  already promoted (an asset already carries its `sourceDocumentId`), so a document
   *  is never re-promotable into a duplicate asset (m8b).
   *
   *  This fan-out can fail for reasons that are NOT "you have no ingest runs": the api
   *  being down, a 500, an expired session, or a response whose shape drifted (a
   *  non-array `/api/ingest` body makes the `for…of` itself throw). The old bare
   *  `catch {}` collapsed all of those into the same silent empty section, so a broken
   *  backend was indistinguishable from an empty inbox. We now record the reason and
   *  let the surface say so — while keeping the failure NON-FATAL: the assets list
   *  above is already hydrated and must still render. */
  private async loadPromotable(): Promise<void> {
    try {
      const alreadyPromoted = new Set(
        this.store.list("alle").value.map((a) => a.sourceDocumentId).filter((id): id is string => Boolean(id)),
      )
      const runs = await apiGet<{ id: string }[]>("/api/ingest")
      // Shape drift is a load failure, not an empty inbox — name it rather than letting
      // the iteration throw an opaque "runs is not iterable" into the catch below.
      if (!Array.isArray(runs)) throw new Error("unerwartete Antwort von /api/ingest")
      const docs: PromotableDoc[] = []
      for (const run of runs) {
        const ingestId = run.id.replace(/^urn:career:ingest:/, "")
        const detail = await apiGet<{ documents: { id: string; kind: string }[] }>(`/api/ingest/${ingestId}`)
        for (const d of detail.documents ?? []) {
          if (!alreadyPromoted.has(d.id)) docs.push({ ingestId, documentId: d.id, kind: d.kind })
        }
      }
      this.promotable.set(docs)
      this.promotableError.set(null)
    } catch (e) {
      // Honest degraded state: we could NOT load this. Distinct from "loaded, nothing to promote".
      this.promotable.set([])
      this.promotableError.set(e instanceof Error ? e.message : String(e))
    }
  }

  confirmAsset(assetId: string): void {
    void this.store.command({ type: "confirm-asset", payload: { assetId } })
  }
  /** Step 1 of the destructive remove: arm the inline confirm (no immediate delete). */
  askRemove(assetId: string): void {
    this.removingId.set(assetId)
  }
  cancelRemove(): void {
    this.removingId.set(null)
  }
  /** Step 2: the user confirmed — issue the tombstone command (optimistic, absolute end-state). */
  removeAsset(assetId: string): void {
    this.removingId.set(null)
    void this.store.command({ type: "remove-asset", payload: { assetId } })
  }
  toggleEdit(assetId: string): void {
    this.editingId.set(this.editingId.value === assetId ? null : assetId)
  }

  async saveEdit(assetId: string, title: string, issuer: string): Promise<void> {
    if (title.trim().length === 0) return
    this.editingId.set(null)
    try {
      await apiPost("/api/assets/update", { assetId, title, ...(issuer.trim() ? { issuer } : {}) })
      await this.refresh()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    }
  }

  async pickAndAdd(): Promise<void> {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = ".pdf,.docx,.md,.txt,image/*"
    input.onchange = () => {
      const file = input.files?.[0]
      if (file) void this.add(file)
    }
    input.click()
  }

  private async add(file: File): Promise<void> {
    this.busy.set(true)
    this.error.set(null)
    try {
      const form = new FormData()
      form.set("file", file)
      form.set("asset", "true")
      form.set("kind", this.addKind.value)
      form.set("title", file.name)
      await apiUpload("/api/documents/upload", form)
      await this.refresh()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.busy.set(false)
    }
  }

  async promote(doc: PromotableDoc): Promise<void> {
    this.busy.set(true)
    this.error.set(null)
    try {
      await apiPost("/api/assets/promote", { ingestId: doc.ingestId, documentId: doc.documentId, kind: "certificate", title: `Dokument (${doc.kind})` })
      await this.refresh()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.busy.set(false)
    }
  }
}

export class CareerAssets extends KitElement<AssetsVm> {
  protected createViewModel(ctx: KitContext): AssetsVm {
    return new AssetsVm(ctx.inject(AssetStoreToken), ctx.inject(SessionToken), ctx.inject(I18nToken))
  }

  private assetCard(vm: AssetsVm, a: AssetView): TemplateResult {
    const editing = vm.editingId.value === a.id
    const removing = vm.removingId.value === a.id
    return html`
      <li urn=${a.urn} class=${classMap({ "asset-card": true, [`is-${a.status}`]: true, "is-removing": removing })}>
        <span class="asset-kind chip" lang="de">${vm.t(KIND_KEY[a.kind])}</span>
        ${editing
          ? html`
              <input class="asset-edit-title" type="text" .value=${a.title} aria-label=${vm.t("asset.editTitle")} />
              <input class="asset-edit-issuer" type="text" .value=${a.issuer ?? ""} aria-label=${vm.t("asset.editIssuer")} placeholder=${vm.t("asset.issuer")} />
            `
          : html`<p class="asset-title" lang="de">${a.title}</p>${a.issuer ? html`<span class="asset-issuer chip" lang="de">${a.issuer}</span>` : nothing}`}
        ${removing
          ? html`
              <div class="asset-remove-confirm" role="group" aria-label=${vm.t("asset.remove.confirmLabel")}>
                <span class="asset-remove-prompt" lang="de">${vm.t("asset.remove.confirmPrompt")}</span>
                <button data-act="confirm-remove-asset" data-asset-id=${a.id} class="asset-remove-yes" lang="de">${vm.t("asset.remove.yes")}</button>
                <button data-act="cancel-remove-asset" data-asset-id=${a.id} lang="de">${vm.t("asset.remove.no")}</button>
              </div>
            `
          : html`
              <div class="asset-actions">
                ${a.status === "proposed"
                  ? html`<button data-act="confirm-asset" data-asset-id=${a.id} lang="de">${vm.t("asset.confirm")}</button>`
                  : html`<span class="asset-confirmed chip is-confirmed" lang="de">${vm.t("asset.confirmed")}</span>`}
                ${editing
                  ? html`<button data-act="save-asset" data-asset-id=${a.id} lang="de">${vm.t("asset.save")}</button>`
                  : html`<button data-act="edit-asset" data-asset-id=${a.id} lang="de">${vm.t("asset.edit")}</button>`}
                <button data-act="remove-asset" data-asset-id=${a.id} class="asset-remove" lang="de">${vm.t("asset.remove")}</button>
              </div>
            `}
      </li>
    `
  }

  /**
   * The »hochgeladene Dokumente übernehmen« section — THREE honest states, not two:
   * a list when there is something to promote, the shared degraded note when the
   * fan-out failed, and (still) nothing at all when it loaded and there is genuinely
   * nothing to promote. The failed state keeps its heading so it reads as part of the
   * document, and carries NO `surface-panel`: §0 forbids fencing this off in a drawn
   * box (the panel wrapper was removed here for exactly that reason).
   */
  private promoteSection(vm: AssetsVm): TemplateResult | typeof nothing {
    const promotable = vm.promotable.value
    const failed = vm.promotableError.value
    if (!failed && promotable.length === 0) return nothing
    return html`
      <section class="asset-promote" aria-labelledby="asset-promote-title">
        <h2 id="asset-promote-title" class="surface-section-title" lang="de">${vm.t("asset.promote.title")}</h2>
        ${failed
          ? degradedNote({
              label: vm.t("asset.promote.failed.label"),
              message: vm.t("asset.promote.failed.message"),
              detail: vm.t("asset.promote.failed.reason", { reason: failed }),
            })
          : html`<ul class="promote-list" role="list">
              ${repeat(promotable, (d) => d.documentId, (d) => html`
                <li class="promote-doc" urn=${`urn:career:document:${d.documentId.replace(/^urn:career:document:/, "")}`}>
                  <span class="promote-doc-kind chip" lang="de">${d.kind}</span>
                  <button data-act="promote-document" data-ingest-id=${d.ingestId} data-document-id=${d.documentId} data-kind=${d.kind} ?disabled=${vm.busy.value} lang="de">${vm.t("asset.promote.action")}</button>
                </li>
              `)}
            </ul>`}
      </section>
    `
  }

  protected render(vm: AssetsVm): TemplateResult {
    const assets = vm.assets.value
    const err = vm.error.value
    return html`
      <section class="surface assets" aria-labelledby="assets-title">
        <h1 id="assets-title" class="surface-title" lang="de">${vm.t("surface.nachweise.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("asset.lead")}</p>
        ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}

        <div class="asset-add" role="group" aria-label=${vm.t("asset.add.group")}>
          <label class="asset-add-kind">
            <span lang="de">${vm.t("asset.add.kindLabel")}</span>
            <select class="asset-kind-select" aria-label=${vm.t("asset.add.kindLabel")}>
              ${ASSET_KINDS.map((k) => html`<option value=${k} ?selected=${vm.addKind.value === k} lang="de">${vm.t(KIND_KEY[k])}</option>`)}
            </select>
          </label>
          <button data-act="upload-asset" data-kind=${vm.addKind.value} ?disabled=${vm.busy.value} class=${classMap({ "is-pending": vm.busy.value })} lang="de">
            ${vm.busy.value ? vm.t("onboarding.uploading") : vm.t("asset.add.upload")}
          </button>
        </div>

        ${assets.length === 0
          ? html`${vm.loaded.value ? html`<p class="surface-empty" lang="de">${vm.t("asset.empty")}</p>` : nothing}`
          : html`<ul class="asset-list" role="list">
              ${repeat(assets, (a) => a.urn, (a) => this.assetCard(vm, a))}
            </ul>`}

        ${this.promoteSection(vm)}
      </section>
    `
  }

  protected override actions(vm: AssetsVm): ActionMap {
    return {
      "upload-asset": (_payload, event) => {
        const select = (event.target as HTMLElement).closest(".asset-add")?.querySelector<HTMLSelectElement>("select.asset-kind-select")
        if (select) vm.addKind.set(select.value as AssetKind)
        void vm.pickAndAdd()
      },
      "confirm-asset": ({ assetId }) => { if (assetId) vm.confirmAsset(assetId) },
      "remove-asset": ({ assetId }) => { if (assetId) vm.askRemove(assetId) },
      "confirm-remove-asset": ({ assetId }) => { if (assetId) vm.removeAsset(assetId) },
      "cancel-remove-asset": () => vm.cancelRemove(),
      "edit-asset": ({ assetId }) => { if (assetId) vm.toggleEdit(assetId) },
      "save-asset": ({ assetId }, event) => {
        if (!assetId) return
        const card = (event.target as HTMLElement).closest(".asset-card")
        const title = card?.querySelector<HTMLInputElement>("input.asset-edit-title")?.value ?? ""
        const issuer = card?.querySelector<HTMLInputElement>("input.asset-edit-issuer")?.value ?? ""
        void vm.saveEdit(assetId, title, issuer)
      },
      "promote-document": ({ ingestId, documentId, kind }) => {
        if (ingestId && documentId) void vm.promote({ ingestId, documentId, kind: kind ?? "" })
      },
    }
  }
}
